import { createFileRoute, Link } from "@tanstack/react-router";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { HubEditorial } from "@/components/site/HubEditorial";
import { HUB_EDITORIAL } from "@/data/hub-editorial";
import { socialImageMeta } from "@/lib/social";
import { ShareBar } from "@/components/site/ShareBar";
import { makeSeoDescription } from "@/lib/seo-description";

const CANONICAL = "https://lifewestway.com/remote-work";
const TITLE = "Remote Work & Jobs 2026 | LifeWestWay";

export const Route = createFileRoute("/remote-work")({
  head: () => ({
    meta: [
      { title: TITLE },
      {
        name: "description",
        content:
          makeSeoDescription("Remote work & job guides for USA, UK, Australia, Canada, Italy & Portugal (2026) — visa-safe remote roles, casual & part-time work, digital nomad income thresholds and where to find hiring employers."),
      },
      { property: "og:title", content: TITLE },
      { property: "og:site_name", content: "LifeWestWay" },
      {
        property: "og:description",
        content:
          makeSeoDescription("Six countries, six 2026 playbooks: remote work, casual jobs, sponsorship & digital nomad income floors in one place."),
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: CANONICAL },
      ...socialImageMeta(),
      { name: "twitter:title", content: TITLE },
    ],
    links: [{ rel: "canonical", href: CANONICAL }],
  }),
  component: RemoteWorkHub,
});

type Guide = {
  country: string;
  flag: string;
  accent: string;
  title: string;
  blurb: string;
  highlight: string;
  to: string;
  params: Record<string, string>;
};

const GUIDES: Guide[] = [
  {
    country: "United States",
    flag: "🇺🇸",
    accent: "from-sky-500/25 to-indigo-400/10",
    title: "Top Remote Jobs Hiring International Talent (2026)",
    blurb:
      "Work-from-anywhere vs sponsorship: how to land US remote roles in AI, cybersecurity and cloud — and convert a contract into an H-1B path.",
    highlight: "Remote → sponsorship playbook",
    to: "/usa/$slug",
    params: { slug: "top-remote-jobs-hiring-international-talent-usa-2026" },
  },
  {
    country: "United Kingdom",
    flag: "🇬🇧",
    accent: "from-indigo-500/25 to-sky-400/10",
    title: "Verified Part-Time & Remote Jobs for Visa Holders (2026)",
    blurb:
      "Student work rights, the £12.71 National Living Wage, PAYE compliance and the Graduate Route — a compliant income strategy for visa holders.",
    highlight: "£12.71/hr National Living Wage",
    to: "/uk/$slug",
    params: { slug: "verified-part-time-remote-jobs-visa-holders-uk-2026" },
  },
  {
    country: "Australia",
    flag: "🇦🇺",
    accent: "from-amber-500/25 to-emerald-400/10",
    title: "How to Find Verified Casual Jobs in Australia (2026)",
    blurb:
      "AUD $26.44 minimum wage, 25% casual loading (≈$33.05/hr), regional work requirements and Fair Work protections decoded.",
    highlight: "AUD $33.05/hr with casual loading",
    to: "/australia/$slug",
    params: { slug: "how-to-find-verified-casual-jobs-australia-2026" },
  },
  {
    country: "Canada",
    flag: "🇨🇦",
    accent: "from-red-500/25 to-rose-400/10",
    title: "On-Campus Jobs & Work Rules for Students (2026)",
    blurb:
      "24-hour off-campus cap, unlimited on-campus hours and the April 2026 co-op permit reform — everything international students must track.",
    highlight: "24hr off-campus / unlimited on-campus",
    to: "/canada/$slug",
    params: { slug: "on-campus-jobs-work-rules-international-students-canada-2026" },
  },
  {
    country: "Italy",
    flag: "🇮🇹",
    accent: "from-emerald-500/25 to-rose-500/10",
    title: "Finding Remote Work in Italy — Digital Nomads (2026)",
    blurb:
      "Two remote-worker categories, ~€28,000 income threshold and the 'highly skilled' rule — plus where global remote-first employers hire.",
    highlight: "~€28,000/yr income floor",
    to: "/italy/$slug",
    params: { slug: "finding-remote-work-italy-digital-nomads-2026" },
  },
  {
    country: "Portugal",
    flag: "🇵🇹",
    accent: "from-emerald-500/25 to-amber-400/10",
    title: "Best Jobs & Remote Work in Lisbon & Porto (2026)",
    blurb:
      "Local salaries vs remote arbitrage, the D8 Digital Nomad Visa (€3,680/mo) and the sectors actually hiring English speakers.",
    highlight: "€3,680/mo D8 income requirement",
    to: "/portugal/$slug",
    params: { slug: "best-jobs-remote-work-lisbon-porto-2026" },
  },
];

function RemoteWorkHub() {
  return (
    <main id="main-content" className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
      <Breadcrumbs
        items={[
          { label: "Home", href: "/" },
          { label: "Remote Work & Jobs" },
        ]}
      />

      {/* Hero */}
      <section className="relative overflow-hidden rounded-3xl border border-border/60 bg-gradient-to-br from-card/80 via-card/40 to-background/60 p-8 sm:p-12">
        <span
          aria-hidden
          className="pointer-events-none absolute -top-24 -right-24 h-64 w-64 rounded-full bg-accent/20 blur-3xl"
        />
        <span
          aria-hidden
          className="pointer-events-none absolute -bottom-24 -left-24 h-64 w-64 rounded-full bg-accent/10 blur-3xl"
        />
        <div className="relative">
          <span className="inline-flex items-center gap-2 rounded-full border border-accent/40 bg-accent/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-accent">
            <span className="h-1.5 w-1.5 rounded-full bg-accent animate-pulse" />
            6 Countries · Updated 2026
          </span>
          <h1 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground">
            Remote Work &amp; <span className="text-accent">Jobs Hub</span>
          </h1>
          <p className="mt-4 max-w-2xl text-base sm:text-lg text-foreground/75">
            Six countries, six 2026 playbooks — visa-safe remote work, casual and part-time
            jobs, digital nomad income thresholds and where international talent actually
            gets hired.
          </p>
        </div>
      </section>

      {/* Grid */}
      <section
        aria-label="Remote work & job guides by country"
        className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3"
      >
        {GUIDES.map((g) => (
          <Link title={`Read the remote work guide: ${g.title}`}
            key={g.title}
            to={g.to}
            params={g.params}
            className="group relative flex flex-col overflow-hidden rounded-2xl border border-border/60 bg-card/50 p-6 transition-all hover:border-accent/60 hover:bg-card/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            <span
              aria-hidden
              className={`pointer-events-none absolute inset-0 bg-gradient-to-br ${g.accent} opacity-0 transition-opacity group-hover:opacity-100`}
            />
            <div className="relative flex items-center justify-between">
              <span className="inline-flex items-center gap-2 rounded-full border border-border/70 bg-background/40 px-3 py-1 text-xs font-semibold text-foreground/80">
                <span aria-hidden className="text-base leading-none">
                  {g.flag}
                </span>
                {g.country}
              </span>
              <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-accent/90">
                2026
              </span>
            </div>

            <h2 className="relative mt-4 font-display text-xl font-bold tracking-tight text-foreground group-hover:text-accent">
              {g.title}
            </h2>
            <p className="relative mt-3 text-sm text-foreground/70">{g.blurb}</p>

            <div className="relative mt-5 flex items-center justify-between border-t border-border/50 pt-4">
              <span className="text-xs font-medium text-foreground/60">
                <span className="text-accent">◆</span> {g.highlight}
              </span>
              <span className="text-sm font-semibold text-accent">
                Read guide <span aria-hidden>→</span>
              </span>
            </div>
          </Link>
        ))}
      </section>

      <HubEditorial data={HUB_EDITORIAL["remote-work"]} />

      <ShareBar />
    </main>
  );
}
